import './InvoicePreview.scss';

import React, { type FC } from 'react';

import { type InvoiceDetails, InvoiceStatus, type IActivityInvoiceData } from '../../interfaces';
import { useTranslation } from 'react-i18next';
import { toCurrencyString } from 'utils';
import useTimezoneUtils from 'hooks/useTimezoneUtils';
import { InvoiceTaskTable } from './InvoiceTaskTable'
import { useBillables } from '../../hooks';
import { Card } from 'components/Shared/Card';
import { COUNTRY_CODE, getAppRegionCode } from 'utils/regionUtils';
import { AURecipientSupplierDetails } from './AURecipientSupplierDetails';
import { isNewZealandProvider } from 'utils/providerUtils';
import { LabelValue } from 'components/Shared/LabelValue';

interface InvoicePreviewHTMLProps {
    invoice: InvoiceDetails
    activity?: IActivityInvoiceData
    showWspTable?: boolean
    activityId?: number 
    activityService?: string 
}

interface InvoiceHeaderProps {
    isDraft: boolean
    isAustralia: boolean
}

export const InvoicePreviewHTML: FC<InvoicePreviewHTMLProps> = ({ invoice, activity, showWspTable, activityId, activityService = '' }) => {
    const { i18n, t } = useTranslation('invoicing', { useSuspense: false });

    const { formatDate } = useTimezoneUtils()

    const formatInvoiceDate = (date: string | Date) => {
        return formatDate(date, 'l')
    }

    const activityIdVal = activity ? activity.activityId : activityId
    const serviceVal = activity ? activity.service : activityService

    const { wspBillables, sessionBillables } = useBillables(invoice?.billables, showWspTable)

    const getSubmittedDate = () => {
        const submittedOrCreatedDate = invoice?.submittedDate ? invoice?.submittedDate : invoice?.createdDate
        return submittedOrCreatedDate ? formatInvoiceDate(new Date(submittedOrCreatedDate)) : 'N/A'
    }

    const renderWspTaskTable = () => {
        if (showWspTable && wspBillables && wspBillables.length > 0) {
            return (
                <InvoiceTaskTable billables={wspBillables} header={t('tasks','Tasks')} isTaskTable/>
            )
        }
    }

    const isAustralia = getAppRegionCode() === COUNTRY_CODE.AUSTRALIA;
    const isNewZealand = isNewZealandProvider(invoice);
    const isDraftInvoice = invoice.statusId === InvoiceStatus.Draft;

    const renderSessionsTable = () => {
        if (invoice && sessionBillables && sessionBillables.length > 0) {
            return (
                <InvoiceTaskTable billables={sessionBillables} header={isAustralia ? 'Supplies made by Supplier to Recipient' : t('sessions','Sessions')} />
            )
        }
    }

    const InvoiceHeader = ({ isDraft, isAustralia }: InvoiceHeaderProps) => {
        const title = isDraft
            ? t('draft_invoice')
            : `${t('invoice')} ${invoice.invoiceId || ""}`;

        return (
            <div>
                {isAustralia && !isDraft && <h2 className="invoice_preview_title global__header2">Recipient Created Tax Invoice</h2>}
                <h2 className="invoice_preview_title global__header2">{title}</h2>
                {isAustralia && isDraft && <h2 className="invoice_preview_title global__header2">Recipient Created Tax Invoice</h2>}
            </div>
        );
    };

    const InvoiceFooter = ({ isAustralia }) => {
        const hasProviderInvoiceNumber = invoice.providerInvoiceNumber && invoice.providerInvoiceNumber.toUpperCase() !== 'N/A';
        const Agreement = () => (
            <div className={hasProviderInvoiceNumber ? "" : "invoice_footer"}>
                <p className="global__paragraph agreement_text">The GST shown will form part of the total GST payable by the supplier on the business activity statement (BAS) for the relevant tax period.</p>

                <h4>Written Agreement</h4>

                <p className="global__paragraph agreement_text">
                    The recipient and the supplier declare that this agreement relates to the above supplies. 
                    The recipient will issue tax invoices in respect of these supplies. 
                    The supplier will not issue tax invoices in respect of these supplies. 
                    The supplier acknowledges that it is registered for GST and that it will notify the recipient if it ceases to be registered. 
                    The recipient acknowledges that it is registered for GST and that it will notify the supplier if it ceases to be registered. 
                    Acceptance of this recipient created tax invoice (RCTI) constitutes acceptance of the terms of this written agreement. 
                    Both parties to this supply agree that they are parties to an RCTI agreement. 
                    The supplier must notify the recipient within 21 days of receiving this document if the supplier does not wish to accept the proposed agreement.
                </p>
            </div>
        )
        return (
            <div className="invoice_preview_text_container">
                {
                hasProviderInvoiceNumber &&
                <LabelValue label={t('provider_invoice_number')} value={invoice.providerInvoiceNumber} />
                }
                {isAustralia && <Agreement />}
            </div>
        ) 
    }
   
    return (
        <Card className="invoice_preview_wrapper">
            <div className="invoice_preview_text_container">
                <InvoiceHeader isAustralia={isAustralia} isDraft={isDraftInvoice} />
                <div className="invoice_submission_date_container">
                    <LabelValue label={t('submission_date')} value={getSubmittedDate()} />
                </div>
                <p className="invoice_preview_provider_name global__header3 well_spaced">{`${invoice.provider.firstName} ${invoice.provider.lastName}`}</p>
                <div className="provider_info_container_wrapper">
                    <div className="provider_info_container provider_info_container_left">
                        <LabelValue label={t('activity_id')} value={activityIdVal} />
                        <LabelValue label={t('service')} value={serviceVal} />
                    </div>
                    <div className="provider_info_container provider_info_container_right">
                        <LabelValue label={t('provider_id')} value={invoice.provider.providerId} />
                        {
                            isNewZealand && 
                            (
                                <LabelValue label={t('gst_number')} value={getGSTNumber(invoice)} />
                            )
                        }
                        <LabelValue label={t('email')} value={invoice.provider.emailAddress} />
                        <LabelValue label={t('phone')} value={invoice.provider.contactInfo.phones[0].phoneNumber} />
                    </div>
                </div>
            </div>
            <div className="invoice_preview_separator"/>
            <div className="invoice_preview_text_container">
                {isAustralia ? (
                     <AURecipientSupplierDetails invoice={invoice} />
                    ) : (
                    <div className="provider_billing_vendor_container_wrapper">
                        <div className="provider_billing_vendor_container provider_billing_vendor_container_left">
                            <div className="billing_address_container">
                                <p className="global__paragraph global__paragraph--light">{t('bill_to')}:</p>   
                                <h3 className="global__header4 billing_company_name">{t('telus_health')}</h3>
                            </div>      
                        </div>
                        <div className="provider_billing_vendor_container provider_billing_vendor_container_right">
                            { 
                                invoice.provider.vendor?.name &&
                                <div className="invoice_preview_associate_vendor_row provider_billing_vendor_row">
                                    <span className="value">{invoice.provider.vendor.name}</span>
                                    <p className="title global__paragraph global__paragraph--light">{t('associated_vendor')}</p>
                                </div>
                            }
                        </div>
                    </div>
                    )
                }
            </div>
            
            {renderSessionsTable()}
            {renderWspTaskTable()}
            
            <div className="invoice_preview_text_container">
                { invoice.addCaseManagementTime && (
                        <LabelValue label={<span className='case_management_time_label'>{t('case_management_time')}</span>} value={<span className='case_management_time_value'>{t('yes')}</span>} />
                    )
                }
                {
                    invoice.taxes?.length > 0 && (
                        <div className="taxes">
                            {invoice.taxes?.map((tax) =>
                                <LabelValue key={tax.taxCode} label={tax.taxDescription} value={<span className="invoice_preview_tax_amount">{toCurrencyString(tax.amount, i18n.language, invoice.currencyCode) ?? '---'}</span>} />
                            )}
                        </div>
                    )
                }
                <div className="totals">

                    <LabelValue label={t('total_fees', 'Total Fees')} value={<span className="total-fees-amount">{toCurrencyString(invoice?.totalFees ?? 0, i18n.language, invoice?.currencyCode) ?? '---'}</span>} />
                    <LabelValue label={t('total_expenses', 'Total Expenses')} value={<span className="total-expenses-amount">{toCurrencyString(invoice?.totalExpenses ?? 0, i18n.language, invoice?.currencyCode) ?? '---'}</span>} />
                </div>
                <div className="invoice_total_amount">
                    <LabelValue label={t('total_invoice_amount')} value={<span className="invoice_preview_total_amount">{toCurrencyString(invoice.grandTotal, i18n.language, invoice.currencyCode) ?? '---'}</span>} />
                </div>
            </div>
            <InvoiceFooter isAustralia={isAustralia} />
        </Card>
    )
}

const getGSTNumber = (invoice: InvoiceDetails): string => invoice.taxes?.[0]?.taxNumber ?? "";